/**
 * JP Gems & Jewels - Sales Order (inside Hariom ERP)
 * -----------------------------------------------------------------------------
 * Party-centric invoice workflow (matches the founder's Excel account sheet):
 *
 *   1. Pick / add a PARTY (client).
 *   2. Keep appending sale lines (requirements) to that party's OPEN invoice,
 *      one by one, over the months — serial numbers auto-increment.
 *   3. When the dues reach a peak, CLOSE the invoice: it is finalised and can be
 *      downloaded as ONE combined PDF / Excel to send to the party for payment.
 *   4. Adding a line again starts a fresh invoice (new number). Each party runs
 *      independently.
 *
 * Shape / Quality / Category / Sieve / Size / Code are pick-lists seeded from the
 * real Excel data — the sales team selects (with a "+" to add a new option).
 * Amounts follow the sheet: Less = Sale x disc%, Amt-6 = Sale x (1-disc%),
 * Sale Amt = Sale x Wt, Inv Amt = Sale Amt x (1-disc%).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  Gem, Plus, Trash2, Edit3, FileText, FileSpreadsheet, Upload,
  ChevronLeft, ChevronRight, UserPlus, Lock, CheckCircle2, Filter, ChevronDown, Search, CalendarClock,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import {
  useSalesBookStore, calcLine, invoiceTotal, type SaleLine, type Invoice,
  SEED_SHAPES, SEED_QUALITIES, SEED_CATEGORIES, SEED_SIEVES, SEED_SIZES, SEED_CODES, SALES_SEED_VERSION,
} from '../../store/salesBookStore'
import { usePriceBookStore, SEED_PRICEBOOK, PRICEBOOK_SEED_VERSION, priceKey } from '../../store/priceBookStore'
import { useDiamondPurchaseStore } from '../../store/diamondPurchaseStore'
import { importPriceChart } from '../../store/importPriceChart'
import { useAuthStore } from '../../store/authStore'

const todayStr = () => new Date().toISOString().slice(0, 10)
const plusDays = (iso: string, days: number) => {
  const d = new Date(iso || todayStr()); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10)
}
// Whole-day gap between two YYYY-MM-DD dates (used to show the due "term" in the +days box).
const diffDays = (from: string, to: string) => {
  const a = Date.parse(from), b = Date.parse(to)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return ''
  return String(Math.round((b - a) / 86400000))
}
const money = (n: number) => '₹' + Math.round(n).toLocaleString('en-IN')
const wt = (n: number) => n.toFixed(2)

interface LineForm {
  date: string; dueDate: string; shape: string; quality: string; category: string
  sieve: string; size: string; code: string; pcs: string; weight: string; saleRate: string; discountPct: string; remarks: string
}
const emptyForm = (): LineForm => ({
  date: todayStr(), dueDate: plusDays(todayStr(), 45), shape: '', quality: '', category: '',
  sieve: '', size: '', code: '', pcs: '', weight: '', saleRate: '', discountPct: '6', remarks: '',
})

/** Select that also lets the employee add a new option on the spot (+ on the right). */
const OptionSelect: React.FC<{
  value: string; onChange: (v: string) => void; options: string[]
  onAdd: (v: string) => void; label: string; transform?: (v: string) => string
}> = ({ value, onChange, options, onAdd, label, transform }) => (
  <div className="flex gap-1 mt-1">
    <select value={value} onChange={e => onChange(e.target.value)} className="form-input flex-1 text-sm">
      <option value="">Select {label}…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
      {value && !options.includes(value) && <option value={value}>{value}</option>}
    </select>
    <button type="button" title={`Add new ${label}`}
      onClick={() => { const raw = window.prompt(`Add new ${label}:`) || ''; const t = (transform ? transform(raw) : raw).trim(); if (t) { onAdd(t); onChange(t) } }}
      className="shrink-0 w-9 rounded-lg bg-primary-100 text-primary-700 hover:bg-primary-200 text-lg font-semibold leading-none">+</button>
  </div>
)

/**
 * Free-type combo: the employee can TYPE any value (e.g. a one-off sieve that
 * differs for every customer) AND still pick from existing options (datalist).
 * The "+" button optionally saves the typed value to the shared list to reuse.
 */
const ComboSelect: React.FC<{
  value: string; onChange: (v: string) => void; options: string[]
  onAdd: (v: string) => void; label: string; listId: string; transform?: (v: string) => string
}> = ({ value, onChange, options, onAdd, label, listId, transform }) => {
  const known = options.includes(value)
  return (
    <div className="flex gap-1 mt-1">
      <input list={listId} value={value}
        onChange={e => onChange(transform ? transform(e.target.value) : e.target.value)}
        placeholder={`Type or pick ${label}…`}
        className="form-input flex-1 min-w-0 text-sm" />
      <datalist id={listId}>{options.map(o => <option key={o} value={o} />)}</datalist>
      <button type="button" title={value && !known ? `Save “${value}” to the ${label} list` : `Type a new ${label}, then press + to save it`}
        disabled={!value || known}
        onClick={() => { const t = (transform ? transform(value) : value).trim(); if (t && !options.includes(t)) onAdd(t) }}
        className={`shrink-0 w-9 rounded-lg text-lg font-semibold leading-none ${value && !known ? 'bg-primary-100 text-primary-700 hover:bg-primary-200' : 'bg-steel-100 text-steel-300 cursor-not-allowed'}`}>+</button>
    </div>
  )
}

/**
 * Column header MULTI-select filter. The employee can tick several values
 * (e.g. two or more dates) and the table shows every matching row. The panel is
 * rendered in a portal (fixed position) so it is never clipped by the table's
 * horizontal scroll container.
 */
const MultiFilter: React.FC<{
  label: string; options: string[]; selected: string[]
  onChange: (vals: string[]) => void; format?: (v: string) => string
}> = ({ label, options, selected, onChange, format }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const active = selected.length > 0
  const openPanel = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 150) })
    }
    setOpen(true)
  }
  const toggleVal = (v: string) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v])
  return (
    <>
      <button ref={btnRef} type="button" onClick={e => { e.stopPropagation(); open ? setOpen(false) : openPanel() }}
        className={clsx('mt-1 w-full flex items-center justify-between gap-1 text-[11px] font-medium rounded border px-1.5 py-0.5',
          active ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-steel-200 bg-white text-steel-600')}>
        <span className="truncate">{active ? `${selected.length} selected` : 'All'}</span>
        <ChevronDown size={11} className="shrink-0" />
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="fixed z-[61] bg-white border border-steel-200 rounded-lg shadow-lg max-h-72 overflow-auto text-xs"
            style={{ top: pos.top, left: pos.left, minWidth: pos.width }}>
            <div className="flex items-center justify-between px-2 py-1.5 border-b border-steel-100 sticky top-0 bg-white">
              <span className="font-semibold text-steel-600">Filter · {label}</span>
              {active && <button onClick={() => onChange([])} className="text-primary-600 hover:underline">Clear</button>}
            </div>
            {options.length === 0 ? <div className="px-2 py-2 text-steel-400">No values</div> : options.map(v => (
              <label key={v} className="flex items-center gap-2 px-2 py-1 hover:bg-steel-50 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={selected.includes(v)} onChange={() => toggleVal(v)} className="accent-primary-600" />
                <span className="text-steel-700">{format ? format(v) : v}</span>
              </label>
            ))}
          </div>
        </>, document.body)}
    </>
  )
}

const SalesOrder: React.FC = () => {
  const s = useSalesBookStore()
  const { invoices, parties, shapes, qualities, categories, sieves, sizes, codes } = s
  const { user } = useAuthStore()
  const priceBook = usePriceBookStore(st => st.book)          // GLOBAL chart baseline (shared fallback)
  const partyPrices = usePriceBookStore(st => st.partyPrices) // per-party custom sale prices (reactive)
  const dpOrders = useDiamondPurchaseStore(st => st.orders)
  // Actual purchase price per Shape|Quality|Slive, taken from real Diamond Purchase
  // lines (source of truth for profit — persists across re-downloads).
  const dpPriceByCombo = useMemo(() => {
    const m: Record<string, number> = {}
    dpOrders.forEach(o => o.lines.forEach(l => { if (l.purchaseRate > 0) m[priceKey(l.shape, l.quality, l.sieve)] = l.purchaseRate }))
    return m
  }, [dpOrders])

  // Seed the pick-lists ONCE (only if empty) — never overwrites data.
  useEffect(() => {
    const seedIfEmpty = () => {
      const st = useSalesBookStore.getState()
      const pb = usePriceBookStore.getState()
      // Seed the shared price book whenever it is empty (robust — always fills the
      // 58 price-chart combinations so Sale/Purchase rates can auto-fill everywhere).
      if (Object.keys(pb.book).length === 0) {
        pb.setBook(SEED_PRICEBOOK); pb.setSeedVersion(PRICEBOOK_SEED_VERSION)
      }
      // Run the pick-list merge ONCE per version — unions the Excel starters AND
      // the price-chart Shape/Quality/Slive values into the dropdowns (deduped),
      // without ever removing anything the team added.
      if (st.seedVersion === SALES_SEED_VERSION) return
      const combos = Object.keys(SEED_PRICEBOOK).map(k => k.split('|'))
      const uni = (a: string[]) => Array.from(new Set(a.filter(Boolean)))
      st.setShapes(uni([...st.shapes, ...SEED_SHAPES, ...combos.map(c => c[0])]))
      st.setQualities(uni([...st.qualities, ...SEED_QUALITIES, ...combos.map(c => c[1])]))
      st.setCategories(uni([...st.categories, ...SEED_CATEGORIES]))
      st.setSieves(SEED_SIEVES)   // exact clean list (replaces the messy ones)
      st.setSizes(SEED_SIZES)     // exact clean list (replaces the messy ones)
      st.setCodes(uni([...st.codes, ...SEED_CODES]))
      st.setSeedVersion(SALES_SEED_VERSION)
    }
    if (useSalesBookStore.persist.hasHydrated()) seedIfEmpty()
    return useSalesBookStore.persist.onFinishHydration(seedIfEmpty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selectedParty, setSelectedParty] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<LineForm>(emptyForm())
  const [editing, setEditing] = useState<{ invId: string; lineId: string } | null>(null)
  const [dueDays, setDueDays] = useState('')             // quick "+N days from date" entry in the modal
  // Per-column MULTI filters for the open-invoice table (key → selected values). Empty = no filter.
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({})
  const setColFilterVals = (key: string, vals: string[]) => setColFilters(f => ({ ...f, [key]: vals }))
  const [partySearch, setPartySearch] = useState('')   // parties-list search box
  const [editingInvId, setEditingInvId] = useState<string | null>(null)   // founder reopened a closed invoice to edit
  const [showDueSummary, setShowDueSummary] = useState(false)   // founder's payment-follow-up popup

  // Auto-fill the Sale Rate when Shape + Quality + Slive match. THIS PARTY'S own
  // saved price wins; if the party has none, fall back to the global chart baseline.
  // A price saved for one party is never used for another.
  useEffect(() => {
    if (!showModal || !form.shape || !form.quality || !form.sieve || !selectedParty) return
    const combo = priceKey(form.shape, form.quality, form.sieve)
    const partySale = partyPrices[selectedParty]?.[combo]
    const sale = (partySale && partySale > 0) ? partySale : (priceBook[combo]?.s ?? 0)
    if (sale > 0) setForm(f => (String(sale) === f.saleRate ? f : { ...f, saleRate: String(sale) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.shape, form.quality, form.sieve, showModal, priceBook, partyPrices, selectedParty])

  // Ensure the selected party always has an OPEN invoice, so its number shows in
  // the header and the Add Line modal even before the first line is added.
  useEffect(() => {
    if (selectedParty) s.ensureOpenInvoice(selectedParty)
    setColFilters({})   // reset table filters when moving between parties
    setEditingInvId(null)   // never carry a "reopened invoice" edit across parties
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParty])

  const openInvoiceOf = (party: string) => invoices.find(i => i.party === party && i.status === 'open')
  const closedOf = (party: string) => invoices.filter(i => i.party === party && i.status === 'closed')

  const summary = useMemo(() => {
    const open = invoices.filter(i => i.status === 'open')
    const closed = invoices.filter(i => i.status === 'closed')
    const dues = open.reduce((t, i) => t + invoiceTotal(i), 0)
    // Payment lifecycle on CLOSED invoices: unpaid = pending receivable, paid = received.
    const received = closed.filter(i => i.paid).reduce((t, i) => t + invoiceTotal(i), 0)
    const pending = closed.filter(i => !i.paid).reduce((t, i) => t + invoiceTotal(i), 0)
    return { parties: parties.length, open: open.length, closed: closed.length, dues, received, pending }
  }, [invoices, parties])

  const importRef = useRef<HTMLInputElement>(null)
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return
    try { const res = await importPriceChart(file); toast.success(`Imported ${res.rows} rows — ${res.combos} priced combinations`) }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Import failed') }
    e.target.value = ''
  }

  const newParty = () => {
    const raw = window.prompt('New party (client) name:') || ''
    const name = raw.trim()
    if (!name) return
    if (parties.includes(name)) { toast('Party already exists'); setSelectedParty(name); return }
    s.addParty(name); setSelectedParty(name); toast.success(`Party "${name}" added`)
  }

  const openNew = () => {
    // Date is always TODAY (when the line is added). Due Date copies the invoice's
    // last due date (so the term the employee picked carries forward); the first
    // line defaults to today + 45 days. Weight & Pcs stay blank (they change each time).
    const open = selectedParty ? openInvoiceOf(selectedParty) : undefined
    const last = open?.lines[open.lines.length - 1]
    setForm(last ? {
      date: todayStr(), dueDate: last.dueDate, shape: last.shape, quality: last.quality, category: last.category,
      sieve: last.sieve, size: last.size, code: last.code, pcs: '', weight: '',
      saleRate: String(last.saleRate), discountPct: String(last.discountPct), remarks: last.remarks,
    } : emptyForm())
    // Prefill the +days box with the due term carried forward (today → sticky due date),
    // so the employee sees the previous days count and can adjust it if needed.
    setDueDays(last ? diffDays(todayStr(), last.dueDate) : '45')
    setEditing(null); setShowModal(true)
  }
  const openEdit = (inv: Invoice, l: SaleLine) => {
    // Show the exact due term this line used (date → due date) in the +days box.
    setDueDays(diffDays(l.date, l.dueDate))
    setForm({
      date: l.date, dueDate: l.dueDate, shape: l.shape, quality: l.quality, category: l.category,
      sieve: l.sieve, size: l.size, code: l.code, pcs: String(l.pcs), weight: String(l.weight),
      saleRate: String(l.saleRate), discountPct: String(l.discountPct), remarks: l.remarks || '',
    })
    setEditing({ invId: inv.id, lineId: l.id }); setShowModal(true)
  }
  // "+" beside Sale Rate — save the current rate as this combination's price.
  const saveComboPrice = () => {
    const r = parseFloat(form.saleRate) || 0
    if (!selectedParty) { toast.error('Open a party first'); return }
    if (!(form.shape && form.quality && form.sieve && r > 0)) { toast.error('Pick shape, quality, slive and a rate first'); return }
    // Saved PER PARTY — this rate applies to this party only, never to others.
    usePriceBookStore.getState().setPartyPrice(selectedParty, priceKey(form.shape, form.quality, form.sieve), r)
    toast.success(`Price saved for ${selectedParty}: ${form.shape} · ${form.quality} · ${form.sieve} = ₹${r}`)
  }

  const saveLine = () => {
    if (!selectedParty) return
    const saleRate = parseFloat(form.saleRate) || 0
    const weight = parseFloat(form.weight) || 0
    if (saleRate <= 0 || weight <= 0) { toast.error('Enter a valid Sale rate and Weight'); return }
    // Discount: BLANK = default 6%; an explicit 0 = NO discount (0%). Any other number is used as-is.
    const discParsed = parseFloat(form.discountPct)
    const discountPct = form.discountPct.trim() === '' ? 6 : (Number.isFinite(discParsed) ? discParsed : 6)
    const line: SaleLine = {
      id: editing?.lineId || `sl-${Date.now()}`,
      date: form.date || todayStr(), dueDate: form.dueDate || plusDays(form.date, 45),
      shape: form.shape.trim(), quality: form.quality.trim(), category: form.category.trim(),
      sieve: form.sieve.trim(), size: form.size.trim(), code: form.code.trim(),
      pcs: parseInt(form.pcs, 10) || 0, weight, saleRate, discountPct,
      remarks: form.remarks.trim(),
      highlight: editing ? (invoices.flatMap(i => i.lines).find(l => l.id === editing.lineId)?.highlight ?? false) : false,
    }
    // Learn this combination's sale price for THIS PARTY the first time it's entered
    // (per-party, so it never leaks to other parties).
    if (selectedParty && line.shape && line.quality && line.sieve && line.saleRate > 0) {
      const k = priceKey(line.shape, line.quality, line.sieve)
      const cur = usePriceBookStore.getState().partyPrices[selectedParty]?.[k]
      if (!cur) usePriceBookStore.getState().setPartyPrice(selectedParty, k, line.saleRate)
    }
    if (editing) { s.updateLine(editing.invId, editing.lineId, line); toast.success('Line updated') }
    else { const invId = s.ensureOpenInvoice(selectedParty); s.addLine(invId, line); toast.success('Line added') }
    setShowModal(false)
  }
  const delLine = (inv: Invoice, l: SaleLine) => { if (window.confirm('Delete this line?')) { s.removeLine(inv.id, l.id); toast.success('Deleted') } }
  const toggleHighlight = (inv: Invoice, l: SaleLine) => s.updateLine(inv.id, l.id, { ...l, highlight: !l.highlight })

  const closeInvoice = (inv: Invoice) => {
    if (!inv.lines.length) { toast.error('Add at least one line before closing'); return }
    if (!window.confirm(`Close invoice ${inv.invoiceNo} for ${inv.party}? It becomes final and ready to send.`)) return
    s.closeInvoice(inv.id); s.ensureOpenInvoice(inv.party)   // start a fresh invoice (new number)
    if (editingInvId === inv.id) setEditingInvId(null)        // done editing a reopened invoice
    toast.success(`${inv.invoiceNo} closed — download the invoice to send`)
  }

  // Same discount rule as saveLine: blank = 6% default, explicit 0 = no discount.
  const discPreview = form.discountPct.trim() === '' ? 6 : (Number.isFinite(parseFloat(form.discountPct)) ? parseFloat(form.discountPct) : 6)
  const preview = calcLine({ saleRate: parseFloat(form.saleRate) || 0, weight: parseFloat(form.weight) || 0, discountPct: discPreview })
  const comboKey = form.shape && form.quality && form.sieve ? priceKey(form.shape, form.quality, form.sieve) : ''
  // This party's saved price (if any); else the global chart baseline.
  const partyComboPrice = comboKey && selectedParty ? partyPrices[selectedParty]?.[comboKey] : undefined
  const comboSale = (partyComboPrice && partyComboPrice > 0) ? partyComboPrice : (comboKey ? (priceBook[comboKey]?.s ?? 0) : 0)
  const activeOpen = selectedParty ? openInvoiceOf(selectedParty) : undefined
  const activeInvNo = activeOpen ? activeOpen.invoiceNo : (selectedParty ? `JP-SO-${String(s.invoiceSeq + 1).padStart(4, '0')}` : '')

  const jpHeader = (doc: jsPDF, W: number) => {
    doc.setFillColor(17, 17, 17); doc.rect(0, 0, W, 26, 'F')
    doc.setDrawColor(232, 190, 140); doc.setLineWidth(0.7)
    doc.lines([[6, 6], [-6, 6], [-6, -6]], 20, 6, [1, 1], 'S', true)
    doc.setTextColor(232, 190, 140); doc.setFont('times', 'bold'); doc.setFontSize(12); doc.text('JP', 17, 14)
    doc.setFont('times', 'bolditalic'); doc.setFontSize(18); doc.text('JP Gems & Jewels', 36, 13)
    doc.setFont('times', 'italic'); doc.setFontSize(8); doc.setTextColor(210, 180, 140); doc.text('Serving Beauty Through Jewellery', 36, 20)
  }

  const invTotals = (inv: Invoice) => inv.lines.reduce((t, l) => {
    const c = calcLine(l)
    return { pcs: t.pcs + l.pcs, weight: t.weight + l.weight, saleAmt: t.saleAmt + c.saleAmt, invAmt: t.invAmt + c.invAmt }
  }, { pcs: 0, weight: 0, saleAmt: 0, invAmt: 0 })

  // Group lines by due date (sorted) — powers the customer payment schedule + party summary.
  const dueGroups = (lines: SaleLine[]) => {
    const m = new Map<string, SaleLine[]>()
    lines.forEach(l => { const a = m.get(l.dueDate) || []; a.push(l); m.set(l.dueDate, a) })
    return [...m.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0]))).map(([due, ls]) => {
      let saleAmt = 0, invAmt = 0
      ls.forEach(l => { const c = calcLine(l); saleAmt += c.saleAmt; invAmt += c.invAmt })
      return { due: due || '—', lines: ls, saleAmt, invAmt }
    })
  }

  // ---- Per-column table filters ----
  // Header label → filter key. Columns NOT listed here get no filter
  // (#, Sieve, Pcs, Wt, Remarks, Flag, actions — per founder's request).
  const FILTER_KEY: Record<string, string> = {
    'Date': 'date', 'Due': 'due', 'Shape': 'shape', 'Quality': 'quality', 'Cat': 'category',
    'Size': 'size', 'Code': 'code', 'Sale ₹': 'sale', 'Less': 'less', 'Amt−': 'amt',
    'Sale Amt': 'saleAmt', 'Inv Amt': 'invAmt',
  }
  // The comparable value of a line for a given filter key (numbers as plain strings).
  const lineFieldValue = (l: SaleLine, key: string): string => {
    const c = calcLine(l)
    switch (key) {
      case 'date': return l.date
      case 'due': return l.dueDate
      case 'shape': return l.shape
      case 'quality': return l.quality
      case 'category': return l.category
      case 'size': return l.size
      case 'code': return l.code
      case 'sale': return String(l.saleRate)
      case 'less': return String(Math.round(c.less))
      case 'amt': return String(Math.round(c.rateAfter))
      case 'saleAmt': return String(Math.round(c.saleAmt))
      case 'invAmt': return String(Math.round(c.invAmt))
      default: return ''
    }
  }
  const NUMERIC_KEYS = new Set(['sale', 'less', 'amt', 'saleAmt', 'invAmt'])
  const filterOptions = (lines: SaleLine[], key: string) => {
    const vals = Array.from(new Set(lines.map(l => lineFieldValue(l, key)).filter(Boolean)))
    return NUMERIC_KEYS.has(key) ? vals.sort((a, b) => Number(a) - Number(b)) : vals.sort()
  }
  const lineMatchesFilters = (l: SaleLine) =>
    Object.entries(colFilters).every(([k, vals]) => !vals || vals.length === 0 || vals.includes(lineFieldValue(l, k)))
  const activeFilterCount = Object.values(colFilters).filter(v => v && v.length > 0).length

  // customer=true → CUSTOMER copy: the internal "Code" column is removed.
  const downloadPdf = (inv: Invoice, customer = false) => {
    if (!inv.lines.length) { toast.error('No lines to export'); return }
    const t = invTotals(inv)
    const doc = new jsPDF({ orientation: 'landscape' })
    const W = doc.internal.pageSize.getWidth()
    jpHeader(doc, W)
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold'); doc.setFontSize(12)
    doc.text(customer ? 'Customer Invoice' : 'Sales Invoice', 14, 36)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9)
    doc.text(`Party: ${inv.party}`, 14, 42); doc.text(`Invoice: ${inv.invoiceNo}`, 90, 42)
    doc.text(`Date: ${inv.createdDate}`, 160, 42); doc.text(`Status: ${inv.status.toUpperCase()}`, 220, 42)
    const strip = (a: string[]) => (customer ? a.filter((_, i) => i !== 8) : a)   // index 8 = Code column
    // PDF-safe money: the built-in font can't render the ₹ glyph (it prints as a
    // stray superscript and breaks column width), so amounts use plain grouped digits.
    const inr = (n: number) => Math.round(n).toLocaleString('en-IN')
    // Customer copy is ordered by due date (so the payment schedule reads cleanly).
    const bodyLines = customer ? dueGroups(inv.lines).flatMap(g => g.lines) : inv.lines
    // Numeric columns (Pcs → Inv Amt) are right-aligned so the digits line up.
    // Indexes shift by 1 on the customer copy because the Code column is removed.
    const numFirst = customer ? 8 : 9          // Pcs
    const numLast = customer ? 14 : 15         // Inv Amt
    const numCols: Record<number, { halign: 'right' | 'center' }> = { 0: { halign: 'center' } }
    for (let k = numFirst; k <= numLast; k++) numCols[k] = { halign: 'right' }
    autoTable(doc, {
      startY: 46,
      head: [strip(['Sr', 'Date', 'Due', 'Shape', 'Quality', 'Cat', 'Sieve', 'Size', 'Code', 'Pcs', 'Wt', 'Sale', 'Less', 'Amt-', 'Sale Amt', 'Inv Amt', 'Remarks'])],
      body: bodyLines.map((l, i) => {
        const c = calcLine(l)
        return strip([String(i + 1), l.date, l.dueDate, l.shape, l.quality, l.category, l.sieve, l.size, l.code,
          String(l.pcs), wt(l.weight), inr(l.saleRate), inr(c.less), inr(c.rateAfter),
          inr(c.saleAmt), inr(c.invAmt), l.remarks])
      }),
      foot: [strip(['', '', '', '', '', '', '', 'TOTAL', '', String(t.pcs), wt(t.weight), '', '', '', inr(t.saleAmt), inr(t.invAmt), ''])],
      showFoot: 'lastPage',   // grand TOTAL prints ONCE, on the final page (no misleading per-page total)
      styles: { fontSize: 6.5, cellPadding: 1, overflow: 'linebreak' },
      headStyles: { fillColor: [31, 56, 100], halign: 'center' },
      footStyles: { fillColor: [17, 17, 17], textColor: [232, 190, 140], fontStyle: 'bold', halign: 'right' },
      columnStyles: numCols,
      // Flagged lines are shaded red so they stand out for the customer.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didParseCell: (d: any) => { if (d.section === 'body' && bodyLines[d.row.index]?.highlight) d.cell.styles.fillColor = [254, 226, 226] },
    })
    let y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || 60
    doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.text(`Total Dues: Rs. ${inr(t.invAmt)}`, 14, y + 8)
    if (!customer) { doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(`Generated by: ${user?.name || '-'}`, 120, y + 8) }
    // CUSTOMER copy: payment schedule grouped by due date, so the party sees what is due & when.
    if (customer) {
      const groups = dueGroups(inv.lines)
      autoTable(doc, {
        startY: y + 12,
        head: [['DUE REPORT (payment schedule)', '', '']],
        body: [
          ['Due Date', 'Total Sale Amt (Rs.)', 'Amount Due Rs. (Inv)'],
          ...groups.map(g => [g.due, inr(g.saleAmt), inr(g.invAmt)]),
          ['TOTAL', inr(t.saleAmt), inr(t.invAmt)],
        ],
        styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
        headStyles: { fillColor: [31, 56, 100], halign: 'left' },
        columnStyles: { 0: { cellWidth: 60 }, 1: { halign: 'right', cellWidth: 55 }, 2: { halign: 'right', cellWidth: 55 } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        didParseCell: (d: any) => {
          if (d.section === 'body' && d.row.index === 0) { d.cell.styles.fillColor = [17, 17, 17]; d.cell.styles.textColor = [232, 190, 140]; d.cell.styles.fontStyle = 'bold' }
          if (d.section === 'body' && d.row.index === groups.length + 1) { d.cell.styles.fillColor = [17, 17, 17]; d.cell.styles.textColor = [232, 190, 140]; d.cell.styles.fontStyle = 'bold' }
        },
      })
      y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || y
    }
    doc.save(`JP_${inv.invoiceNo}_${inv.party.replace(/\s+/g, '_')}${customer ? '_Customer' : ''}.pdf`)
    toast.success(customer ? 'Customer invoice PDF downloaded' : 'Invoice PDF downloaded')
  }

  const downloadExcel = (inv: Invoice, customer = false) => {
    if (!inv.lines.length) { toast.error('No lines to export'); return }
    const t = invTotals(inv)
    const blank: Record<string, string | number> = {
      Flag: '', Sr: '', Date: '', 'Due Date': '', Shape: '', Quality: '', Category: '', Sieve: '', Size: '',
      ...(customer ? {} : { Code: '' }), Pcs: '', Wt: '', Sale: '', Less: '', 'Amt After -': '', 'Sale Amt': '', 'Inv Amt After -': '', Remarks: '',
    }
    const lineRow = (l: SaleLine, i: number): Record<string, string | number> => {
      const c = calcLine(l)
      return {
        Flag: l.highlight ? '★ FLAG' : '', Sr: i + 1, Date: l.date, 'Due Date': l.dueDate, Shape: l.shape, Quality: l.quality, Category: l.category,
        Sieve: l.sieve, Size: l.size, ...(customer ? {} : { Code: l.code }), Pcs: l.pcs, Wt: Number(wt(l.weight)), Sale: l.saleRate,
        'Less': Math.round(c.less), 'Amt After -': Math.round(c.rateAfter), 'Sale Amt': Math.round(c.saleAmt),
        'Inv Amt After -': Math.round(c.invAmt), Remarks: l.remarks,
      }
    }
    const rows: Record<string, string | number>[] = []
    const groups = dueGroups(inv.lines)
    if (customer) {
      // Grouped by due date, with an Inv-Amt subtotal after each due-date group (payment schedule view).
      let sr = 0
      groups.forEach(g => {
        g.lines.forEach(l => rows.push(lineRow(l, sr++)))
        rows.push({ ...blank, Size: `SUBTOTAL (Due ${g.due})`, 'Sale Amt': Math.round(g.saleAmt), 'Inv Amt After -': Math.round(g.invAmt) })
      })
    } else {
      inv.lines.forEach((l, i) => rows.push(lineRow(l, i)))
    }
    rows.push({ ...blank, Size: 'TOTAL', Pcs: t.pcs, Wt: Number(wt(t.weight)), 'Sale Amt': Math.round(t.saleAmt), 'Inv Amt After -': Math.round(t.invAmt) })
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)
    const title = `${inv.party}  |  Invoice ${inv.invoiceNo}  |  ${inv.createdDate}  |  ${inv.status.toUpperCase()}${customer ? '  |  CUSTOMER COPY' : ''}`
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A1' })
    // CUSTOMER copy: append a Due Report (payment schedule) summary below the table.
    if (customer) {
      XLSX.utils.sheet_add_aoa(ws, [
        [],
        ['DUE REPORT (payment schedule)'],
        ['Due Date', 'Total Sale Amt', 'Amount Due (Inv)'],
        ...groups.map(g => [g.due, Math.round(g.saleAmt), Math.round(g.invAmt)]),
        ['TOTAL', Math.round(t.saleAmt), Math.round(t.invAmt)],
      ], { origin: { r: rows.length + 2, c: 0 } })
    }
    XLSX.utils.book_append_sheet(wb, ws, inv.invoiceNo.slice(0, 31))
    XLSX.writeFile(wb, `JP_${inv.invoiceNo}_${inv.party.replace(/\s+/g, '_')}${customer ? '_Customer' : ''}.xlsx`)
    toast.success(customer ? 'Customer invoice Excel downloaded' : 'Invoice Excel downloaded')
  }

  // ---- Estimate (FOUNDER only): per-product profit for this invoice ----
  // Purchase price comes from the shared price book (fed by Diamond Purchase);
  // Purchase Amt = purchase price x weight, Profit = Sale Amt (invoiced) - Purchase Amt.
  // If no purchase price exists for a combination it is treated as 0.
  const estimateRows = (inv: Invoice) => {
    let tPur = 0, tSale = 0, tProfit = 0
    const rows = inv.lines.map((l, i) => {
      const c = calcLine(l)
      const k = priceKey(l.shape, l.quality, l.sieve)
      const purRate = dpPriceByCombo[k] ?? (priceBook[k]?.p ?? 0)   // real purchase first, price book fallback
      const purAmt = purRate * l.weight
      const saleAmt = c.saleAmt        // gross sale = sale rate x weight (before discount), per founder
      const profit = saleAmt - purAmt
      tPur += purAmt; tSale += saleAmt; tProfit += profit
      return { i: i + 1, product: `${l.shape} ${l.quality} ${l.sieve}`.trim(), size: l.size, weight: l.weight, purRate, purAmt, saleRate: l.saleRate, saleAmt, profit }
    })
    return { rows, tPur, tSale, tProfit }
  }

  const downloadEstimatePdf = (inv: Invoice) => {
    if (!inv.lines.length) { toast.error('No lines to export'); return }
    const { rows, tPur, tSale, tProfit } = estimateRows(inv)
    const doc = new jsPDF({ orientation: 'landscape' })
    const W = doc.internal.pageSize.getWidth()
    jpHeader(doc, W)
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text('Estimate', 14, 36)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9)
    doc.text(`Party: ${inv.party}`, 14, 42); doc.text(`Invoice: ${inv.invoiceNo}`, 100, 42); doc.text(`Date: ${inv.createdDate}`, 180, 42)
    autoTable(doc, {
      startY: 46,
      head: [['Sr', 'Product (Shape Quality Slive)', 'Size', 'Wt', 'Purch/ct', 'Purch Amt', 'Sale/ct', 'Sale Amt', 'Profit']],
      body: rows.map(r => [String(r.i), r.product, r.size, wt(r.weight), (Math.round(r.purRate)).toLocaleString('en-IN'), (Math.round(r.purAmt)).toLocaleString('en-IN'), r.saleRate.toLocaleString('en-IN'), (Math.round(r.saleAmt)).toLocaleString('en-IN'), (Math.round(r.profit)).toLocaleString('en-IN')]),
      foot: [['', 'TOTAL', '', '', '', (Math.round(tPur)).toLocaleString('en-IN'), '', (Math.round(tSale)).toLocaleString('en-IN'), (Math.round(tProfit)).toLocaleString('en-IN')]],
      showFoot: 'lastPage',   // grand TOTAL prints ONCE, on the final page
      styles: { fontSize: 7.5, cellPadding: 1.3, overflow: 'linebreak' },
      headStyles: { fillColor: [31, 56, 100], halign: 'center' },
      footStyles: { fillColor: [6, 78, 59], textColor: [209, 250, 229], fontStyle: 'bold', halign: 'right' },
      columnStyles: { 0: { halign: 'center' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' } },
      // Colour the Profit column: green for a gain, red for a loss.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didParseCell: (d: any) => {
        if (d.section === 'body' && d.column.index === 8) {
          const v = parseFloat(String(d.cell.raw).replace(/[^0-9.-]/g, ''))
          if (Number.isFinite(v)) { d.cell.styles.textColor = v < 0 ? [185, 28, 28] : [4, 120, 87]; d.cell.styles.fontStyle = 'bold' }
        }
      },
    })
    doc.save(`JP_${inv.invoiceNo}_${inv.party.replace(/\s+/g, '_')}_Estimate.pdf`)
    toast.success('Estimate PDF downloaded')
  }

  const downloadEstimateExcel = (inv: Invoice) => {
    if (!inv.lines.length) { toast.error('No lines to export'); return }
    const { rows, tPur, tSale, tProfit } = estimateRows(inv)
    const data: Record<string, string | number>[] = rows.map(r => ({
      Sr: r.i, Product: r.product, Size: r.size, Wt: Number(wt(r.weight)),
      'Purchase/ct': Math.round(r.purRate), 'Purchase Amt': Math.round(r.purAmt),
      'Sale/ct': r.saleRate, 'Sale Amt': Math.round(r.saleAmt), Profit: Math.round(r.profit),
    }))
    data.push({ Sr: '', Product: 'TOTAL', Size: '', Wt: '', 'Purchase/ct': '', 'Purchase Amt': Math.round(tPur), 'Sale/ct': '', 'Sale Amt': Math.round(tSale), Profit: Math.round(tProfit) })
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(data)
    XLSX.utils.sheet_add_aoa(ws, [[`ESTIMATE (FOUNDER)  |  ${inv.party}  |  ${inv.invoiceNo}  |  ${inv.createdDate}`]], { origin: 'A1' })
    XLSX.utils.book_append_sheet(wb, ws, 'Estimate')
    XLSX.writeFile(wb, `JP_${inv.invoiceNo}_${inv.party.replace(/\s+/g, '_')}_Estimate.xlsx`)
    toast.success('Estimate Excel downloaded')
  }

  // =========================================================================
  //  LEVEL 2 — one party's account (open invoice + closed history)
  // =========================================================================
  if (selectedParty) {
    // Normally the party's OPEN invoice. If the founder reopened a closed one to
    // edit, show THAT invoice instead so edits land on the right document.
    const open = (editingInvId ? invoices.find(i => i.id === editingInvId && i.status === 'open') : undefined) || openInvoiceOf(selectedParty)
    const closed = closedOf(selectedParty)
    const t = open ? invTotals(open) : { pcs: 0, weight: 0, saleAmt: 0, invAmt: 0 }
    return (
      <div className="space-y-5">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelectedParty(null)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-600" title="Back to parties"><ChevronLeft size={20} /></button>
            <div>
              <h1 className="page-title flex items-center gap-2">
                <Gem size={20} className="text-primary-600" /> {selectedParty}
                {open && open.lines.length > 0 && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200">
                    Due {money(t.invAmt)}
                  </span>
                )}
              </h1>
              <p className="page-subtitle">Running sales invoice — add requirements one by one, then close to generate the final invoice</p>
              {open && open.lines.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-steel-500 font-medium">Due schedule:</span>
                  {dueGroups(open.lines).map(g => (
                    <span key={g.due} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                      {g.due} · {money(g.invAmt)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <button onClick={openNew} className="btn-primary text-sm flex items-center gap-2"><Plus size={15} /> Add Line</button>
        </div>

        {open ? (
          <div className="card p-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-steel-200 bg-steel-50">
              <div className="flex items-center gap-3">
                <span className="text-xs text-steel-500">Invoice No</span>
                <input value={open.invoiceNo} onChange={e => s.setInvoiceNo(open.id, e.target.value)} className="font-mono text-sm font-semibold text-primary-700 bg-white border border-steel-200 rounded px-2 py-0.5 w-36" />
                <span className="text-xs text-steel-500">· {open.createdDate}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">OPEN</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(open)} title="Internal copy (with Code)" className="text-xs px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-1"><FileText size={13} /> PDF</button>
                <button onClick={() => downloadExcel(open)} title="Internal copy (with Code)" className="text-xs px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center gap-1"><FileSpreadsheet size={13} /> Excel</button>
                <button onClick={() => downloadPdf(open, true)} title="Customer invoice (no Code column)" className="text-xs px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 flex items-center gap-1"><FileText size={13} /> Customer PDF</button>
                <button onClick={() => downloadExcel(open, true)} title="Customer invoice (no Code column)" className="text-xs px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-1"><FileSpreadsheet size={13} /> Cust XLS</button>
                {user?.role === 'admin' && (<>
                  <button onClick={() => downloadEstimatePdf(open)} title="Estimate — profit per product (founder only)" className="text-xs px-2.5 py-1.5 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 flex items-center gap-1"><FileText size={13} /> Estimate PDF</button>
                  <button onClick={() => downloadEstimateExcel(open)} title="Estimate — profit per product (founder only)" className="text-xs px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 flex items-center gap-1"><FileSpreadsheet size={13} /> Estimate XLS</button>
                </>)}
                <button onClick={() => closeInvoice(open)} className="text-xs px-2.5 py-1.5 bg-steel-800 text-white rounded-lg hover:bg-steel-900 flex items-center gap-1"><Lock size={13} /> Close &amp; Generate</button>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-primary-50 border-b border-primary-100 text-xs">
                <Filter size={13} className="text-primary-600" />
                <span className="text-primary-700 font-medium">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active:</span>
                {Object.entries(colFilters).filter(([, v]) => v && v.length > 0).map(([k, vals]) => {
                  const label = Object.keys(FILTER_KEY).find(h => FILTER_KEY[h] === k) || k
                  const shown = vals.map(v => NUMERIC_KEYS.has(k) ? Number(v).toLocaleString('en-IN') : v).join(', ')
                  return (
                    <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-primary-200 text-primary-700">
                      {label}: <b className="max-w-[220px] truncate" title={shown}>{shown}</b>
                      <button onClick={() => setColFilterVals(k, [])} className="hover:text-red-600" title="Remove">×</button>
                    </span>
                  )
                })}
                <button onClick={() => setColFilters({})} className="ml-auto text-primary-600 hover:text-primary-800 underline font-medium">Clear all</button>
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-steel-50 border-b border-steel-200">
                  {['#', 'Date', 'Due', 'Shape', 'Quality', 'Cat', 'Sieve', 'Size', 'Code', 'Pcs', 'Wt', 'Sale ₹', 'Less', 'Amt−', 'Sale Amt', 'Inv Amt', 'Remarks', 'Flag', ''].map((h, i) => {
                    const fkey = FILTER_KEY[h]
                    return (
                    <th key={i} className={clsx('table-header whitespace-nowrap align-top', i >= 9 && i <= 15 && 'text-right', i === 17 && 'text-center')}>
                      {h}
                      {fkey && (
                        <MultiFilter label={h} options={filterOptions(open.lines, fkey)} selected={colFilters[fkey] || []}
                          onChange={vals => setColFilterVals(fkey, vals)}
                          format={v => NUMERIC_KEYS.has(fkey) ? Number(v).toLocaleString('en-IN') : v} />
                      )}
                    </th>
                  )})}
                </tr></thead>
                <tbody>
                  {open.lines.length === 0 ? (
                    <tr><td colSpan={19} className="text-center text-steel-400 py-10">No lines yet. Click “Add Line” to start this invoice.</td></tr>
                  ) : open.lines.filter(lineMatchesFilters).length === 0 ? (
                    <tr><td colSpan={19} className="text-center text-steel-400 py-10">No lines match the active filters. <button onClick={() => setColFilters({})} className="text-primary-600 underline">Clear all filters</button></td></tr>
                  ) : open.lines.map((l, idx) => ({ l, idx })).filter(({ l }) => lineMatchesFilters(l)).map(({ l, idx }) => {
                    const i = idx
                    const c = calcLine(l)
                    return (
                      <tr key={l.id} className={clsx('table-row-hover', l.highlight && 'bg-red-50')}>
                        <td className="table-cell text-center text-steel-400">{i + 1}</td>
                        <td className="table-cell whitespace-nowrap text-steel-500">{l.date}</td>
                        <td className="table-cell whitespace-nowrap text-steel-500">{l.dueDate}</td>
                        <td className="table-cell font-medium text-steel-800 whitespace-nowrap">{l.shape || '—'}</td>
                        <td className="table-cell whitespace-nowrap">{l.quality || '—'}</td>
                        <td className="table-cell whitespace-nowrap">{l.category || '—'}</td>
                        <td className="table-cell whitespace-nowrap">{l.sieve || '—'}</td>
                        <td className="table-cell whitespace-nowrap">{l.size || '—'}</td>
                        <td className="table-cell whitespace-nowrap">{l.code || '—'}</td>
                        <td className="table-cell text-right">{l.pcs || '—'}</td>
                        <td className="table-cell text-right">{wt(l.weight)}</td>
                        <td className="table-cell text-right">{l.saleRate.toLocaleString('en-IN')}</td>
                        <td className="table-cell text-right text-steel-500">{Math.round(c.less).toLocaleString('en-IN')}</td>
                        <td className="table-cell text-right text-steel-500">{Math.round(c.rateAfter).toLocaleString('en-IN')}</td>
                        <td className="table-cell text-right whitespace-nowrap">{money(c.saleAmt)}</td>
                        <td className="table-cell text-right font-semibold text-green-700 whitespace-nowrap">{money(c.invAmt)}</td>
                        <td className="table-cell max-w-[180px] truncate text-steel-600" title={l.remarks}>{l.remarks || '—'}</td>
                        <td className="table-cell text-center">
                          <button onClick={() => toggleHighlight(open, l)} title="Flag this line to clarify with the customer (shows on downloads)" className={clsx('px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap', l.highlight ? 'bg-red-600 text-white' : 'bg-steel-100 text-steel-500 hover:bg-red-100 hover:text-red-600')}>{l.highlight ? '● Flagged' : 'Flag'}</button>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => openEdit(open, l)} className="p-1 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={13} /></button>
                            {user?.role === 'admin' && (
                              <button onClick={() => delLine(open, l)} className="p-1 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete (founder only)"><Trash2 size={13} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                {open.lines.length > 0 && (() => {
                  const shown = open.lines.filter(lineMatchesFilters)
                  const filtered = activeFilterCount > 0
                  const ft = filtered ? invTotals({ ...open, lines: shown }) : t
                  const fc = filtered ? shown.length : open.lines.length
                  return (
                  <tfoot>
                    <tr className="bg-steel-900 text-white font-semibold">
                      <td className="px-3 py-2 text-right whitespace-nowrap" colSpan={9}>{filtered ? `FILTERED TOTAL (${fc} of ${open.lines.length} lines)` : `TOTAL (${fc} lines)`}</td>
                      <td className="px-3 py-2 text-right">{ft.pcs.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right">{wt(ft.weight)}</td>
                      <td className="px-3 py-2" colSpan={3}></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{money(ft.saleAmt)}</td>
                      <td className="px-3 py-2 text-right text-amber-300 whitespace-nowrap">{money(ft.invAmt)}</td>
                      <td className="px-3 py-2" colSpan={3}></td>
                    </tr>
                  </tfoot>
                  )
                })()}
              </table>
            </div>
            {open.lines.length > 0 && (
              <div className="px-4 py-2 flex justify-end">
                <button onClick={openNew} className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"><Plus size={14} /> Add next line</button>
              </div>
            )}
          </div>
        ) : (
          <div className="card text-center py-10 text-steel-400 text-sm">Preparing invoice…</div>
        )}

        {closed.length > 0 && (
          <div className="card p-0 overflow-hidden">
            <p className="px-4 py-2.5 text-sm font-semibold text-steel-700 border-b border-steel-200 flex items-center gap-2"><CheckCircle2 size={15} className="text-green-600" /> Closed Invoices ({closed.length})</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-steel-50 border-b border-steel-200">
                  <th className="table-header">Invoice No</th><th className="table-header">Closed</th>
                  <th className="table-header text-center">Lines</th><th className="table-header text-right">Total Dues</th>
                  <th className="table-header text-center">Payment</th>
                  <th className="table-header text-center">Download / Remove</th>
                </tr></thead>
                <tbody>
                  {closed.map(inv => (
                    <tr key={inv.id} className={clsx('table-row-hover', inv.paid && 'bg-green-50/50')}>
                      <td className="table-cell font-mono text-xs font-medium text-primary-700">{inv.invoiceNo}</td>
                      <td className="table-cell text-xs text-steel-500">{inv.closedDate}</td>
                      <td className="table-cell text-center">{inv.lines.length}</td>
                      <td className="table-cell text-right font-bold">{money(invoiceTotal(inv))}</td>
                      <td className="table-cell text-center">
                        {/* Pay button: Yes → payment received (leaves Pending, joins Payment Received); No → back to pending. */}
                        {inv.paid ? (
                          <button
                            onClick={() => { if (window.confirm(`Mark ${inv.invoiceNo} as UNPAID (move back to pending)?`)) { s.setInvoicePaid(inv.id, false); toast.success(`${inv.invoiceNo} moved to pending`) } }}
                            title={`Paid on ${inv.paidDate || '—'} — click to undo`}
                            className="text-xs px-2.5 py-1 rounded-full bg-green-600 text-white hover:bg-green-700 inline-flex items-center gap-1">✓ Paid</button>
                        ) : (
                          <button
                            onClick={() => { if (window.confirm(`Payment received for ${inv.invoiceNo} (${money(invoiceTotal(inv))})?\n\nYes = mark as paid (removed from pending dues).`)) { s.setInvoicePaid(inv.id, true); toast.success(`${inv.invoiceNo} marked as paid ✓`) } }}
                            title="Mark this invoice as payment received"
                            className="text-xs px-2.5 py-1 rounded-full bg-orange-100 text-orange-700 border border-orange-300 hover:bg-orange-200 inline-flex items-center gap-1">Pay ▸</button>
                        )}
                      </td>
                      <td className="table-cell text-center">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          <button onClick={() => downloadPdf(inv)} title="Internal (with Code)" className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"><FileText size={11} /> PDF</button>
                          <button onClick={() => downloadExcel(inv)} title="Internal (with Code)" className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-1"><FileSpreadsheet size={11} /> Excel</button>
                          <button onClick={() => downloadPdf(inv, true)} title="Customer invoice (no Code)" className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center gap-1"><FileText size={11} /> Customer PDF</button>
                          <button onClick={() => downloadExcel(inv, true)} title="Customer invoice (no Code)" className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100 flex items-center gap-1"><FileSpreadsheet size={11} /> Cust XLS</button>
                          {user?.role === 'admin' && (<>
                            <button onClick={() => downloadEstimatePdf(inv)} title="Estimate — profit (founder only)" className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 flex items-center gap-1"><FileText size={11} /> Estimate PDF</button>
                            <button onClick={() => downloadEstimateExcel(inv)} title="Estimate — profit (founder only)" className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 flex items-center gap-1"><FileSpreadsheet size={11} /> Estimate XLS</button>
                          </>)}
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Reopen ${inv.invoiceNo} to edit? It moves back to the OPEN invoice above — fix lines / discounts, then press "Close & Generate" to finalise it again.`)) {
                                  s.reopenInvoice(inv.id)
                                  setEditingInvId(inv.id)
                                  window.scrollTo({ top: 0, behavior: 'smooth' })
                                  toast.success(`${inv.invoiceNo} reopened — edit above, then Close & Generate`)
                                }
                              }}
                              title="Edit this invoice (founder only) — reopens it to fix lines / apply a special discount"
                              className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center gap-1"><Edit3 size={11} /> Edit</button>
                          )}
                          {user?.role === 'admin' && (
                            <button onClick={() => { if (window.confirm(`Delete ${inv.invoiceNo}?`)) { s.removeInvoice(inv.id); toast.success('Deleted') } }} title="Delete (founder only)" className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={13} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && renderModal()}
      </div>
    )
  }

  // =========================================================================
  //  Add / Edit line modal
  // =========================================================================
  function renderModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModal(false)}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-steel-800">{editing ? 'Edit Line' : 'Add Line'} — {selectedParty}
              <span className="ml-2 font-mono text-sm text-primary-700">{activeInvNo}</span></h3>
            <button onClick={() => setShowModal(false)} className="text-steel-400 hover:text-steel-700 text-2xl leading-none">×</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-steel-600">Date</label>
              <input type="date" value={form.date} onChange={e => {
                const d = e.target.value; const n = parseInt(dueDays, 10)
                // Changing the Date keeps the due term (days), so the Due Date shifts with it.
                setForm({ ...form, date: d, dueDate: Number.isFinite(n) ? plusDays(d, n) : (form.dueDate || plusDays(d, 45)) })
              }} className="form-input w-full mt-1 text-sm" /></div>
            <div><label className="text-xs font-medium text-steel-600">Due Date <span className="text-steel-400 font-normal">(days / date)</span></label>
              <div className="flex items-stretch gap-1.5 mt-1">
                <input type="number" min="0" value={dueDays} placeholder="days"
                  title="Days from the Date (auto-filled from the previous line) — type to set the Due Date"
                  onChange={e => {
                    const v = e.target.value; setDueDays(v)
                    const n = parseInt(v, 10)
                    if (Number.isFinite(n)) setForm(f => ({ ...f, dueDate: plusDays(f.date || todayStr(), n) }))
                  }}
                  className="form-input shrink-0 w-16 text-sm text-center px-1" />
                <input type="date" value={form.dueDate}
                  onChange={e => { const dd = e.target.value; setForm({ ...form, dueDate: dd }); setDueDays(diffDays(form.date || todayStr(), dd)) }}
                  className="form-input flex-1 min-w-0 text-sm" />
              </div>
              {dueDays !== '' && Number.isFinite(parseInt(dueDays, 10)) && (
                <p className="text-[11px] text-primary-600 mt-0.5">{parseInt(dueDays, 10)} days → {form.dueDate}</p>
              )}
            </div>
            <div><label className="text-xs font-medium text-steel-600">Shape</label>
              <OptionSelect value={form.shape} onChange={v => setForm({ ...form, shape: v })} options={shapes} onAdd={s.addShape} label="shape" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Quality</label>
              <ComboSelect value={form.quality} onChange={v => setForm({ ...form, quality: v })} options={qualities} onAdd={s.addQuality} label="quality" listId="so-quality-list" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Category</label>
              <ComboSelect value={form.category} onChange={v => setForm({ ...form, category: v })} options={categories} onAdd={s.addCategory} label="category" listId="so-category-list" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Sieve / Slive</label>
              <ComboSelect value={form.sieve} onChange={v => setForm({ ...form, sieve: v })} options={sieves} onAdd={s.addSieve} label="sieve" listId="so-sieve-list" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Size</label>
              <OptionSelect value={form.size} onChange={v => setForm({ ...form, size: v })} options={sizes} onAdd={s.addSize} label="size" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Code</label>
              <OptionSelect value={form.code} onChange={v => setForm({ ...form, code: v })} options={codes} onAdd={s.addCode} label="code" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Pcs</label>
              <input type="number" value={form.pcs} onChange={e => setForm({ ...form, pcs: e.target.value })} placeholder="0" className="form-input w-full mt-1 text-sm" /></div>
            <div><label className="text-xs font-medium text-steel-600">Weight (ct) *</label>
              <input type="number" step="0.01" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} placeholder="0.00" className="form-input w-full mt-1 text-sm" /></div>
            <div><label className="text-xs font-medium text-steel-600">Sale Rate ₹ * <span className="text-steel-400 font-normal">(auto)</span></label>
              <div className="flex gap-1 mt-1">
                <input type="number" value={form.saleRate} onChange={e => setForm({ ...form, saleRate: e.target.value })} placeholder="auto from price book" className="form-input flex-1 text-sm" />
                <button type="button" title={`Save this rate for ${selectedParty || 'this party'} · Shape · Quality · Slive`} onClick={saveComboPrice} className="shrink-0 w-9 rounded-lg bg-primary-100 text-primary-700 hover:bg-primary-200 text-lg font-semibold leading-none">+</button>
              </div>
              {comboKey && (comboSale > 0
                ? <p className="text-[11px] text-green-600 mt-1">✓ Auto-filled{partyComboPrice && partyComboPrice > 0 ? ` (saved for ${selectedParty})` : ' (chart baseline)'}</p>
                : <p className="text-[11px] text-amber-600 mt-1">No saved price for {selectedParty || 'this party'} · this Shape·Quality·Slive — type it once, then press + to remember it for this party.</p>)}
            </div>
            <div><label className="text-xs font-medium text-steel-600">Discount % <span className="text-steel-400 font-normal">(blank = 6%, 0 = none)</span></label>
              <input type="number" step="0.1" value={form.discountPct} onChange={e => setForm({ ...form, discountPct: e.target.value })} placeholder="6" className="form-input w-full mt-1 text-sm" /></div>
          </div>
          <div>
            <label className="text-xs font-medium text-steel-600">Remarks (shown on the invoice)</label>
            <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. urgent, partial lot, hold for approval…" className="form-input w-full mt-1 text-sm" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-steel-50 rounded-lg p-3 text-sm">
            <div><p className="text-xs text-steel-500">Less {discPreview}%</p><p className="font-semibold">{money(preview.less)}</p></div>
            <div><p className="text-xs text-steel-500">Amt after −{discPreview}%</p><p className="font-semibold">{money(preview.rateAfter)}</p></div>
            <div><p className="text-xs text-steel-500">Sale Amt</p><p className="font-semibold">{money(preview.saleAmt)}</p></div>
            <div><p className="text-xs text-steel-500">Invoice Amt</p><p className="font-bold text-green-700">{money(preview.invAmt)}</p></div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowModal(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={saveLine} className="btn-primary text-sm">{editing ? 'Save Changes' : 'Add Line'}</button>
          </div>
        </div>
      </div>
    )
  }

  // =========================================================================
  //  LEVEL 1 — parties list
  // =========================================================================
  const partyRows = parties.map(p => {
    const open = openInvoiceOf(p)
    const groups = open && open.lines.length ? dueGroups(open.lines) : []
    const nextDue = groups[0]   // earliest due date (groups are sorted ascending)
    const closedInvs = closedOf(p)
    const paidAmt = closedInvs.filter(i => i.paid).reduce((t, i) => t + invoiceTotal(i), 0)
    const pendingAmt = closedInvs.filter(i => !i.paid).reduce((t, i) => t + invoiceTotal(i), 0)
    return { party: p, open, dues: open ? invoiceTotal(open) : 0, lines: open ? open.lines.length : 0, closed: closedInvs.length, nextDue, dueCount: groups.length, paidAmt, pendingAmt }
  })
  // Party-list search (name contains, case-insensitive).
  const visiblePartyRows = partySearch.trim()
    ? partyRows.filter(r => r.party.toLowerCase().includes(partySearch.trim().toLowerCase()))
    : partyRows

  // ---- Due Summary (payment follow-up) ----
  // Every OUTSTANDING invoice (open, or closed-and-unpaid), broken down by due
  // date exactly like the customer PDF's "Due Report", grouped per party and
  // sorted by the earliest due date so the founder sees who to chase first.
  // Recomputes on every render, so closing/paying an invoice updates it live.
  const dueSummary = (() => {
    const outstanding = invoices.filter(i => i.lines.length > 0 && !(i.status === 'closed' && i.paid))
    const map = new Map<string, { party: string; rows: { invoiceNo: string; status: string; paid?: boolean; due: string; saleAmt: number; invAmt: number }[]; total: number; sale: number; earliest: string }>()
    outstanding.forEach(inv => {
      dueGroups(inv.lines).forEach(g => {
        const e = map.get(inv.party) || { party: inv.party, rows: [], total: 0, sale: 0, earliest: '' }
        e.rows.push({ invoiceNo: inv.invoiceNo, status: inv.status, paid: inv.paid, due: g.due, saleAmt: g.saleAmt, invAmt: g.invAmt })
        e.total += g.invAmt; e.sale += g.saleAmt
        map.set(inv.party, e)
      })
    })
    const parties = [...map.values()].map(e => {
      e.rows.sort((a, b) => String(a.due).localeCompare(String(b.due)))
      e.earliest = e.rows[0]?.due || ''
      return e
    }).sort((a, b) => String(a.earliest).localeCompare(String(b.earliest)))
    return {
      parties,
      grandTotal: parties.reduce((t, e) => t + e.total, 0),
      grandSale: parties.reduce((t, e) => t + e.sale, 0),
      invoiceCount: outstanding.length,
    }
  })()

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><Gem size={22} className="text-primary-600" /> Sales Order</h1>
          <p className="page-subtitle">JP Gems &amp; Jewels — one running invoice per party; keep adding requirements, then close &amp; send</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Due Summary — one-click payment follow-up view across every party/invoice. */}
          <button onClick={() => setShowDueSummary(true)} title="See all parties' invoices and due dates for payment follow-up"
            className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 font-medium border border-amber-300"><CalendarClock size={15} /> Due Summary</button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          <button onClick={() => importRef.current?.click()} title="Import price chart (Shape · Quality · Slive → purchase/sale)" className="btn-secondary text-sm flex items-center gap-2"><Upload size={14} /> Import Prices</button>
          <button onClick={newParty} className="btn-primary text-sm flex items-center gap-2"><UserPlus size={15} /> New Party</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Parties</p><p className="text-xl font-bold">{summary.parties}</p></div>
        <div className="card py-3 border-l-4 border-l-amber-500"><p className="text-xs text-steel-500">Open Invoices</p><p className="text-xl font-bold">{summary.open}</p></div>
        <div className="card py-3 border-l-4 border-l-primary-500"><p className="text-xs text-steel-500">Open Dues</p><p className="text-xl font-bold text-primary-600">{money(summary.dues)}</p></div>
        <div className="card py-3 border-l-4 border-l-orange-500"><p className="text-xs text-steel-500">Pending Payment</p><p className="text-xl font-bold text-orange-600">{money(summary.pending)}</p></div>
        {/* Payment Done — highlighted so the founder sees received money at a glance. */}
        <div className="card py-3 border-l-4 border-l-green-500 bg-green-50/60"><p className="text-xs text-green-700 font-medium">💰 Payment Received</p><p className="text-xl font-extrabold text-green-700">{money(summary.received)}</p></div>
        <div className="card py-3 border-l-4 border-l-steel-400"><p className="text-xs text-steel-500">Closed Invoices</p><p className="text-xl font-bold">{summary.closed}</p></div>
      </div>

      <div className="card p-0 overflow-hidden">
        {/* Party search — quickly find a client when there are 100s of parties. */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-steel-200 bg-steel-50">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-steel-400" />
            <input value={partySearch} onChange={e => setPartySearch(e.target.value)}
              placeholder="Search party by name…"
              className="w-full pl-9 pr-8 py-1.5 text-sm border border-steel-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-400" />
            {partySearch && (
              <button onClick={() => setPartySearch('')} title="Clear"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-steel-400 hover:text-red-600 text-base leading-none">×</button>
            )}
          </div>
          <span className="text-xs text-steel-500">{visiblePartyRows.length} of {partyRows.length} parties</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-steel-50 border-b border-steel-200">
              <th className="table-header">Party</th><th className="table-header">Open Invoice</th>
              <th className="table-header text-center">Open Lines</th><th className="table-header text-right">Open Dues</th>
              <th className="table-header text-center">Next Due</th>
              <th className="table-header text-center">Closed</th>
              <th className="table-header text-center">Payment Status</th>
              <th className="table-header text-center">Action</th>
            </tr></thead>
            <tbody>
              {partyRows.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-steel-400 py-10">No parties yet. Click “New Party” to add your first client.</td></tr>
              ) : visiblePartyRows.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-steel-400 py-10">No party matches “{partySearch}”. <button onClick={() => setPartySearch('')} className="text-primary-600 underline">Clear search</button></td></tr>
              ) : visiblePartyRows.map(r => (
                <tr key={r.party} className="table-row-hover cursor-pointer" onClick={() => setSelectedParty(r.party)}>
                  <td className="table-cell font-medium text-steel-800">{r.party}</td>
                  <td className="table-cell font-mono text-xs text-primary-700">{r.open ? r.open.invoiceNo : '—'}</td>
                  <td className="table-cell text-center">{r.lines || '—'}</td>
                  <td className="table-cell text-right font-semibold">{r.dues ? money(r.dues) : '—'}</td>
                  <td className="table-cell text-center">
                    {r.nextDue ? (
                      <span className="inline-flex flex-col items-center leading-tight">
                        <span className="text-[11px] font-semibold text-amber-800">{r.nextDue.due}</span>
                        <span className="text-[10px] text-steel-500">{money(r.nextDue.invAmt)}{r.dueCount > 1 ? ` · +${r.dueCount - 1}` : ''}</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="table-cell text-center">{r.closed || '—'}</td>
                  <td className="table-cell text-center">
                    {r.closed === 0 ? <span className="text-steel-400 text-xs">—</span> : (
                      <span className="inline-flex flex-col items-center leading-tight gap-0.5">
                        {r.pendingAmt > 0
                          ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">Pending {money(r.pendingAmt)}</span>
                          : <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">✓ All paid</span>}
                        {r.paidAmt > 0 && <span className="text-[10px] text-green-600">received {money(r.paidAmt)}</span>}
                      </span>
                    )}
                  </td>
                  <td className="table-cell text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={e => { e.stopPropagation(); setSelectedParty(r.party) }} className="text-xs px-2.5 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 inline-flex items-center gap-1">Open <ChevronRight size={12} /></button>
                      {user?.role === 'admin' && (
                        <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete party "${r.party}" and ALL its invoices? This cannot be undone.`)) { s.deleteParty(r.party); toast.success('Party deleted') } }} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete party (founder only)"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== DUE SUMMARY — payment follow-up popup ===== */}
      {showDueSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowDueSummary(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-steel-200">
              <div>
                <h3 className="text-lg font-bold text-steel-800 flex items-center gap-2"><CalendarClock size={18} className="text-amber-600" /> Due Summary — Payment Follow-up</h3>
                <p className="text-xs text-steel-500">Outstanding invoices by due date (earliest first) — updates live as invoices close or are paid.</p>
              </div>
              <button onClick={() => setShowDueSummary(false)} className="text-steel-400 hover:text-steel-700 text-2xl leading-none">×</button>
            </div>

            <div className="grid grid-cols-3 gap-3 px-5 py-3 bg-steel-50 border-b border-steel-200">
              <div><p className="text-[11px] text-steel-500">Total Due (Inv Amt)</p><p className="text-lg font-bold text-primary-600">{money(dueSummary.grandTotal)}</p></div>
              <div><p className="text-[11px] text-steel-500">Outstanding Invoices</p><p className="text-lg font-bold">{dueSummary.invoiceCount}</p></div>
              <div><p className="text-[11px] text-steel-500">Parties to Follow Up</p><p className="text-lg font-bold">{dueSummary.parties.length}</p></div>
            </div>

            <div className="overflow-y-auto p-4 space-y-4">
              {dueSummary.parties.length === 0 ? (
                <div className="text-center text-steel-400 py-12">🎉 No outstanding dues — every invoice is paid.</div>
              ) : dueSummary.parties.map(e => (
                <div key={e.party} className="border border-steel-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-steel-50 border-b border-steel-200">
                    <span className="font-semibold text-steel-800 flex items-center gap-2"><Gem size={13} className="text-primary-600" /> {e.party}</span>
                    <span className="text-xs text-steel-600">Earliest due <b className="text-amber-700">{e.earliest}</b> · Total due <b className="text-primary-700">{money(e.total)}</b></span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-white border-b border-steel-100">
                        <th className="table-header">Invoice No</th>
                        <th className="table-header text-center">Status</th>
                        <th className="table-header text-center">Due Date</th>
                        <th className="table-header text-right">Sale Amt</th>
                        <th className="table-header text-right">Amount Due (Inv)</th>
                      </tr></thead>
                      <tbody>
                        {e.rows.map((r, i) => (
                          <tr key={i} className="table-row-hover">
                            <td className="table-cell font-mono text-xs text-primary-700">{r.invoiceNo}</td>
                            <td className="table-cell text-center"><span className={clsx('text-[10px] px-1.5 py-0.5 rounded-full font-medium', r.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-steel-200 text-steel-600')}>{r.status === 'open' ? 'OPEN' : 'CLOSED'}</span></td>
                            <td className="table-cell text-center font-medium text-amber-800 whitespace-nowrap">{r.due}</td>
                            <td className="table-cell text-right text-steel-600 whitespace-nowrap">{money(r.saleAmt)}</td>
                            <td className="table-cell text-right font-semibold text-green-700 whitespace-nowrap">{money(r.invAmt)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr className="bg-steel-900 text-white font-semibold">
                        <td className="px-3 py-1.5 text-right" colSpan={3}>PARTY TOTAL</td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">{money(e.sale)}</td>
                        <td className="px-3 py-1.5 text-right text-amber-300 whitespace-nowrap">{money(e.total)}</td>
                      </tr></tfoot>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            {dueSummary.parties.length > 0 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-steel-200 bg-steel-50">
                <span className="text-sm text-steel-600">Grand total across all parties</span>
                <span className="text-base font-bold text-primary-700">Sale {money(dueSummary.grandSale)} · Due {money(dueSummary.grandTotal)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesOrder
